import { AfterViewChecked, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Observable, of, Subscription, throwError } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { VeraService } from 'src/app/services/vera/vera.service';
import { VeraConversationService } from 'src/app/services/vera/vera-conversation.service';
import { getFirebaseBackend } from 'src/app/authUtils';
import { EventService } from 'src/app/core/services/event.service';
import { VeraMessage, extractOptions, normalizeOptions, optionLabel, renderMessageText } from './vera-message.utils';

/** localStorage key used to remember that the user hid the floating widget. */
const VERA_HIDDEN_KEY = 'vera_widget_hidden';
/** Route whose full-page view replaces the floating widget, so the widget must stay hidden there. */
const VERA_FULL_PAGE_ROUTE = '/vera-ai';

@Component({
	selector: 'app-vera-chatbot',
	templateUrl: './vera-chatbot.component.html',
	styleUrls: ['./vera-chatbot.component.scss'],
})
export class VeraChatbotComponent implements OnInit, AfterViewChecked, OnDestroy {
	@ViewChild('messagesEnd') messagesEnd: ElementRef<HTMLDivElement>;

	isOpen = false;
	loading = false;
	draft = '';
	messages: VeraMessage[] = [];
	/** True when the user chose to hide the floating widget; persisted so it stays hidden across reloads. */
	hidden = false;
	/** True while the standalone Vera page is active; the floating widget hides itself to avoid duplicating the UI. */
	isFullPageRoute = false;

	readonly suggestionChips = ['What is the status of my leads?', 'Show me my recent activity', 'Help me with a campaign'];

	private userName = '';
	private userId: number | null = null;
	/** Backing conversation row in vera_conversations; null until the first message of a new conversation is sent. */
	private conversationId: number | null = null;
	private conversationLoaded = false;
	private shouldScroll = false;
	private readonly subscriptions = new Subscription();

	constructor(
		private veraService: VeraService,
		private veraConversationService: VeraConversationService,
		private router: Router,
		private eventService: EventService
	) {}

	ngOnInit(): void {
		const authUser = getFirebaseBackend()?.getAuthenticatedUser();
		this.userName = authUser?.FirstName || '';
		const parsedId = Number(authUser?.IdUser);
		this.userId = Number.isFinite(parsedId) && parsedId > 0 ? parsedId : null;

		this.hidden = localStorage.getItem(VERA_HIDDEN_KEY) === 'true';
		this.isFullPageRoute = this.router.url.startsWith(VERA_FULL_PAGE_ROUTE);

		this.subscriptions.add(
			this.router.events.pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd)).subscribe((event) => {
				this.isFullPageRoute = event.urlAfterRedirects.startsWith(VERA_FULL_PAGE_ROUTE);
				if (this.isFullPageRoute) {
					this.isOpen = false;
				}
			})
		);

		this.subscriptions.add(this.eventService.subscribe('vera:open-widget', () => this.openFromExternal()));
		this.subscriptions.add(
			this.eventService.subscribe('vera:conversation-changed', (payload: { conversationId: number | null }) => this.syncConversation(payload?.conversationId ?? null))
		);
	}

	ngOnDestroy(): void {
		this.subscriptions.unsubscribe();
	}

	/** Hides the floating widget (launcher + panel) until the user reopens it from the topbar. */
	hideWidget(event?: Event): void {
		event?.stopPropagation();
		this.hidden = true;
		this.isOpen = false;
		localStorage.setItem(VERA_HIDDEN_KEY, 'true');
	}

	/** Triggered by the topbar icon: un-hides the widget and opens the chat panel. */
	private openFromExternal(): void {
		this.hidden = false;
		localStorage.removeItem(VERA_HIDDEN_KEY);
		if (this.isFullPageRoute) {
			return;
		}
		this.isOpen = true;
		if (!this.conversationLoaded) {
			this.conversationLoaded = true;
			this.loadLatestConversation();
		}
	}

	ngAfterViewChecked(): void {
		if (this.shouldScroll) {
			this.scrollToBottom();
			this.shouldScroll = false;
		}
	}

	toggle(): void {
		this.isOpen = !this.isOpen;
		if (this.isOpen && !this.conversationLoaded) {
			this.conversationLoaded = true;
			this.loadLatestConversation();
		}
	}

	close(): void {
		this.isOpen = false;
	}

	newConversation(): void {
		this.conversationId = null;
		this.messages = [];
		this.pushAssistant(this.greeting());
	}

	/** Keeps the sidebar in step with the active conversation chosen in the full-page Vera view. */
	private syncConversation(conversationId: number | null): void {
		if (conversationId === this.conversationId) {
			return;
		}
		this.conversationId = conversationId;
		this.conversationLoaded = true;
		if (conversationId) {
			this.loadMessages(conversationId);
		} else {
			this.messages = [];
			this.pushAssistant(this.greeting());
		}
	}

	/** Opens the full-page Vera view, carrying over the active conversation if there is one. */
	expand(): void {
		this.close();
		this.router.navigate(['/vera-ai'], this.conversationId ? { queryParams: { conversation: this.conversationId } } : {});
	}

	sendChip(text: string): void {
		this.draft = text;
		this.send();
	}

	onInputKeydown(event: KeyboardEvent): void {
		if (event.key === 'Enter' && !event.shiftKey) {
			event.preventDefault();
			this.send();
		}
	}

	send(): void {
		const text = this.draft.trim();
		if (!text || this.loading) {
			return;
		}

		this.pushUser(text);
		this.draft = '';
		this.loading = true;

		this.ensureConversationId(text).subscribe({
			next: (conversationId) => {
				this.persistMessage(conversationId, 'user', text);
				this.veraService.sendMessage(text, String(conversationId)).subscribe({
					next: (res) => this.handleResponse(res, conversationId),
					error: () => this.handleError(),
				});
			},
			error: () => this.handleError(),
		});
	}

	/** Called when the user taps one of the option buttons attached to an assistant message. */
	selectOption(message: VeraMessage, option: Record<string, any>): void {
		if (this.loading || message.optionsResolved) {
			return;
		}

		message.optionsResolved = true;

		const label = optionLabel(option);
		this.pushUser(label);
		this.loading = true;

		this.ensureConversationId(label).subscribe({
			next: (conversationId) => {
				this.persistMessage(conversationId, 'user', label);
				this.veraService.sendMessage(label, String(conversationId), option).subscribe({
					next: (res) => this.handleResponse(res, conversationId),
					error: () => this.handleError(),
				});
			},
			error: () => this.handleError(),
		});
	}

	private handleResponse(res: { reply?: string; message?: string; output?: string; options?: Record<string, any> | Record<string, any>[] } | null | undefined, conversationId: number): void {
		this.loading = false;
		const rawText = res?.reply || res?.message || res?.output || 'I did not get a response from the assistant.';
		const { text, options } = extractOptions(rawText);
		const finalOptions = options ?? normalizeOptions(res?.options);
		this.pushAssistant(text, finalOptions);
		this.persistMessage(conversationId, 'assistant', text, finalOptions);
	}

	private handleError(): void {
		this.loading = false;
		this.pushAssistant('I could not connect to the assistant right now. Please try again in a few seconds.');
	}

	render(text: string): string {
		return renderMessageText(text);
	}

	optionLabel(option: Record<string, any>): string {
		return optionLabel(option);
	}

	trackByIndex(index: number): number {
		return index;
	}

	private greeting(): string {
		return `Hi${this.userName ? ' ' + this.userName : ''}! I'm Vera, your LMS assistant. How can I help you today?`;
	}

	/** Loads the user's most recent conversation (if any) so reopening the widget resumes where it left off. */
	private loadLatestConversation(): void {
		if (!this.userId) {
			this.pushAssistant(this.greeting());
			return;
		}

		this.veraConversationService.listConversations(this.userId).subscribe({
			next: (conversations) => {
				if (!conversations?.length) {
					this.pushAssistant(this.greeting());
					return;
				}
				this.conversationId = conversations[0].id;
				this.loadMessages(this.conversationId);
			},
			error: (err) => {
				console.error('Vera: error listing conversations', err);
				this.pushAssistant(this.greeting());
			},
		});
	}

	private loadMessages(conversationId: number): void {
		this.veraConversationService.getMessages(conversationId).subscribe({
			next: (records) => {
				if (!records?.length) {
					this.pushAssistant(this.greeting());
					return;
				}
				this.messages = records.map((record) => ({
					role: record.role,
					text: record.message,
					time: this.formatTime(record.created_at),
					options: record.options ?? undefined,
					optionsResolved: record.role === 'assistant' && record.options?.length ? true : undefined,
				}));
				this.shouldScroll = true;
			},
			error: (err) => {
				console.error('Vera: error loading messages', err);
				this.pushAssistant(this.greeting());
			},
		});
	}

	/** Returns the active conversation id, creating a new row on the backend on first use. */
	private ensureConversationId(firstMessage: string): Observable<number> {
		if (this.conversationId) {
			return of(this.conversationId);
		}

		if (!this.userId) {
			return throwError(() => new Error('No authenticated user'));
		}

		const title = firstMessage.length > 40 ? `${firstMessage.slice(0, 40)}…` : firstMessage;
		return this.veraConversationService.createConversation(this.userId, title).pipe(
			map((res) => {
				this.conversationId = res.id;
				return res.id;
			})
		);
	}

	private persistMessage(conversationId: number, role: 'user' | 'assistant', text: string, options?: Record<string, any>[]): void {
		if (!this.userId) {
			return;
		}
		this.veraConversationService.saveMessage(conversationId, this.userId, role, text, options).subscribe({ error: (err) => console.error('Vera: error saving message', err) });
	}

	private pushUser(text: string): void {
		this.messages.push({ role: 'user', text, time: this.now() });
		this.shouldScroll = true;
	}

	private pushAssistant(text: string, options?: Record<string, any>[]): void {
		this.messages.push({ role: 'assistant', text, time: this.now(), options: options?.length ? options : undefined });
		this.shouldScroll = true;
	}

	private now(): string {
		return new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
	}

	private formatTime(iso: string): string {
		const date = new Date(iso);
		return isNaN(date.getTime()) ? this.now() : date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
	}

	private scrollToBottom(): void {
		try {
			this.messagesEnd?.nativeElement?.scrollIntoView({ behavior: 'smooth', block: 'end' });
		} catch {
			// ignore
		}
	}
}
